import { Editor as CoreEditor } from '@tiptap/core'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, Extension, ReactRenderer, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { getBaseInputClasses } from '~/@/lib/style-utils'
import { cn } from '~/@/lib/utils'
import {
  BreadcrumbObjectType,
  useCreateCommentMutation,
  useGetActiveUsersQuery,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { controlKey, localKeyMap } from '~/common/utils/keymap'
import { ActiveUser } from '~/components/settings/types'
import { PaginatedCommentSingleComment } from '../types'
import { MentionList } from './MentionList'
import { commentIconPlugin, parseMentions } from './utils'

interface CommentFieldProps {
  objectId?: string
  objectType?: BreadcrumbObjectType
  parentCommentId?: string
  comment?: PaginatedCommentSingleComment
  placeholder?: string
}

interface MentionListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean
}

/**
 * Renders a text field for adding a comment
 */
export function CommentField({
  objectId,
  objectType,
  parentCommentId,
  placeholder,
}: CommentFieldProps) {
  const navigate = useNavigate()
  const { data } = useGetActiveUsersQuery()
  const { t } = useTypedTranslation()
  const [createComment] = useCreateCommentMutation({
    onCompleted: (data) => {
      // If the comment was created successfully, update the URL to include the commentId
      // This will scroll the comment into view
      if (data.createComment.id) {
        const pathname = window.location.pathname
        const query = new URLSearchParams(window.location.search)
        query.set('commentId', data.createComment.id)
        navigate(`${pathname}?${query.toString()}`, { replace: true })
      }
    },
  })

  const handleCommentSubmit = useCallback(
    async (editor: CoreEditor) => {
      if (!objectId || !objectType) {
        return
      }
      const text = editor.getText()

      if (!text) {
        return
      }

      const mentions = parseMentions(editor.state.doc)

      await createComment({
        variables: {
          input: {
            objectId,
            objectType,
            text,
            parentCommentId,
            mentionedUserIds: mentions,
          },
        },
      })

      // Clear the editor content
      editor.commands.setContent('')
    },
    [createComment, objectId, objectType, parentCommentId],
  )

  // Allow for self mentions
  const activeUsers = useMemo(() => data?.activeUsers || [], [data?.activeUsers])

  const editor = useEditor(
    {
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      editorProps: {
        attributes: {
          class: cn(getBaseInputClasses(), 'flex flex-col justify-center relative pe-10'),
        },
      },
      onCreate({ editor }: { editor: CoreEditor }) {
        editor.registerPlugin(commentIconPlugin(editor, () => handleCommentSubmit(editor)))
      },
      extensions: [
        StarterKit,
        Mention.configure({
          HTMLAttributes: { class: 'text-blue-9 bg-blue-2 p-1 rounded-sm' },
          renderText({ node }) {
            return `{{${node.attrs.id}}}`
          },
          suggestion: {
            items: ({ query }) => {
              return activeUsers.filter(
                (user) =>
                  `${user.firstName} ${user.lastName}`
                    .toLowerCase()
                    .includes(query.toLowerCase()) ||
                  user.email?.toLowerCase().includes(query.toLowerCase()),
              )
            },

            render: () => {
              let reactRenderer: ReactRenderer<MentionListRef>

              return {
                onStart: (props: SuggestionProps<ActiveUser>) => {
                  reactRenderer = new ReactRenderer(MentionList, {
                    props: {
                      ...props,
                      open: true,
                    },
                    editor: props.editor,
                  })
                },

                onUpdate(props: SuggestionProps<ActiveUser>) {
                  reactRenderer.updateProps({
                    ...props,
                    open: true,
                  })
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                  if (props.event.key === localKeyMap.ESC) {
                    reactRenderer.updateProps({ ...props, open: false })
                    return true
                  }

                  if (props.event.key === localKeyMap.ENTER) {
                    const handled = reactRenderer.ref?.onKeyDown(props) ?? false
                    if (handled) {
                      props.event.preventDefault()
                      props.event.stopPropagation()
                      return true
                    }
                  }

                  const handled = reactRenderer.ref?.onKeyDown(props) ?? false

                  return handled
                },

                onExit(props) {
                  reactRenderer.updateProps({ ...props, open: false })
                  reactRenderer.destroy()
                },
              }
            },
          },
        }),
        Placeholder.configure({
          placeholder:
            placeholder || t(`comments.placeholder`, { controlKey: controlKey('Enter') }),
        }),
        Extension.create({
          addKeyboardShortcuts() {
            return {
              'Mod-Enter': ({ editor }) => {
                handleCommentSubmit(editor)
                return true
              },
            }
          },
        }),
      ],
    },
    [activeUsers, handleCommentSubmit],
  )

  return (
    <div className="relative w-full">
      <EditorContent editor={editor} />
    </div>
  )
}
